import { useTranslation } from 'react-i18next';
import { formatCurrency } from '~/utils';
import type { OrderItem } from '~/pages/order-view/sections/PackingListView';
import type {
  ComparisonRow,
  DiffStatus,
} from '~/pages/order-view/sections/packingListDiff';
import { DIFF_BADGE } from '~/pages/order-view/sections/packingListDiff';

/** Narrower than the full generated `PackingList` type — only the fields this renderer reads, so it can be reused for any packed-items list (e.g. a single shipment's packing list). */
export type PackedItem = {
  id: string;
  price: { amount: number; currency: string };
  quantity: { value: number; unit: OrderItem['quantity']['unit'] };
  platformProductsLengths: {
    id: string;
    lengthDisplayValue: string;
    platformProduct: { name: string };
  };
};

type OrderItemSummary = {
  platformProductsLengths: {
    id: string;
    lengthDisplayValue: string;
    platformProduct: { name: string };
  };
  price?: { amount: number; currency: string } | null;
  quantity: { value: number; unit: OrderItem['quantity']['unit'] };
};

type PackedItemsListProps = Readonly<{
  packingList: { items: PackedItem[] };
  orderItemByPplId: ReadonlyMap<string, OrderItemSummary>;
  diffByPplId: Map<string, ComparisonRow>;
}>;

export function PackedItemsList({
  packingList,
  orderItemByPplId,
  diffByPplId,
}: PackedItemsListProps) {
  const { t } = useTranslation('order');
  const DIFF_LABELS: Record<DiffStatus, string> = {
    MATCHED: t('order.packing_list.diff_matched'),
    QUANTITY_CHANGED: t('order.packing_list.diff_qty_changed'),
    REMOVED: t('order.packing_list.diff_removed'),
    NET_NEW: t('order.packing_list.diff_net_new'),
  };
  return (
    <>
      {packingList.items.map((item) => {
        const orderItem = orderItemByPplId.get(item.platformProductsLengths.id);
        const diff = diffByPplId.get(item.platformProductsLengths.id);
        const badge = diff ? DIFF_BADGE[diff.status] : null;
        return (
          <div
            key={item.id}
            className="p-6 border-b border-gray-100 last:border-b-0"
          >
            <div className="flex gap-4">
              {/* Product icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <span
                  className="text-2xl"
                  role="img"
                  aria-label={t('order.packing_list.timber_aria_label')}
                >
                  🌲
                </span>
              </div>
              {/* Product info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm text-gray-900 font-medium">
                    {item.platformProductsLengths.platformProduct.name} &ndash;{' '}
                    {item.platformProductsLengths.lengthDisplayValue}
                  </h4>
                  {badge && diff && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                    >
                      {DIFF_LABELS[diff.status]}
                    </span>
                  )}
                  {diff?.status === 'QUANTITY_CHANGED' &&
                    diff.packedQty !== undefined &&
                    diff.orderedQty !== undefined && (
                      <span className="text-xs text-amber-700 font-medium ml-1">
                        {diff.packedQty - diff.orderedQty > 0 ? '+' : ''}
                        {(diff.packedQty - diff.orderedQty)
                          .toFixed(3)
                          .replace(/\.?0+$/, '')}{' '}
                        {item.quantity.unit}
                      </span>
                    )}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                  {orderItem ? (
                    <span>
                      {t('order.packing_list.ordered_label')}{' '}
                      <span className="font-medium text-gray-900">
                        {orderItem.quantity.value} {orderItem.quantity.unit}
                      </span>
                    </span>
                  ) : (
                    <span>
                      {t('order.packing_list.ordered_label')}{' '}
                      <span className="font-medium text-gray-900">–</span>
                    </span>
                  )}
                  <span>
                    {t('order.packing_list.packed_label')}{' '}
                    <span className="font-medium text-gray-900">
                      {item.quantity.value} {item.quantity.unit}
                    </span>
                  </span>
                </div>
              </div>
              {/* Price (line total) */}
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-gray-900">
                  {formatCurrency(
                    item.price.amount * item.quantity.value,
                    item.price.currency
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatCurrency(item.price.amount, item.price.currency)}/
                  {item.quantity.unit}
                </p>
              </div>
            </div>
          </div>
        );
      })}

      {/* REMOVED rows: order items absent from the packing list */}
      {[...orderItemByPplId.entries()]
        .filter(([pplId]) => diffByPplId.get(pplId)?.status === 'REMOVED')
        .map(([pplId, orderItem]) => {
          const badge = DIFF_BADGE['REMOVED'];
          return (
            <div
              key={pplId}
              className="p-6 border-b border-gray-100 last:border-b-0 bg-red-50/30"
            >
              <div className="flex gap-4">
                {/* Product icon */}
                <div className="w-16 h-16 bg-gradient-to-br from-red-50 to-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span
                    className="text-2xl"
                    role="img"
                    aria-label={t('order.packing_list.timber_aria_label')}
                  >
                    🌲
                  </span>
                </div>
                {/* Product info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm text-gray-900 font-medium line-through">
                      {orderItem.platformProductsLengths.platformProduct.name}{' '}
                      &ndash;{' '}
                      {orderItem.platformProductsLengths.lengthDisplayValue}
                    </h4>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                    >
                      {DIFF_LABELS['REMOVED']}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                    <span>
                      {t('order.packing_list.ordered_label')}{' '}
                      <span className="font-medium text-gray-900">
                        {orderItem.quantity.value} {orderItem.quantity.unit}
                      </span>
                    </span>
                    <span>
                      {t('order.packing_list.packed_label')}{' '}
                      <span className="font-medium text-gray-400">—</span>
                    </span>
                  </div>
                </div>
                {/* Ordered line total (struck through) */}
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-400 line-through">
                    {formatCurrency(
                      (orderItem.price?.amount ?? 0) * orderItem.quantity.value,
                      orderItem.price?.currency ?? 'USD'
                    )}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
    </>
  );
}
